/**
 * Single source of truth for the locales Emburse Spend supports, shared by the
 * server (locale detection / bootstrap injection) and the client (i18next init).
 *
 * Catalogs are keyed by region-qualified BCP-47 tags — `fr-CA` and `fr-FR` are
 * DISTINCT catalogs. English (`en-US`) ships first; `fr-CA` is the first translation
 * target. Region tags are added here as their catalogs land.
 *
 * This file must not import anything outside of `shared/` so it stays safe to use
 * on both the server and the client.
 */
enum SUPPORTED_LOCALE_ENUM {
  EN_US = 'en-US',
  FR_CA = 'fr-CA'
}
export const SUPPORTED_LOCALES = Object.values(SUPPORTED_LOCALE_ENUM);

export type SupportedLocale = `${SUPPORTED_LOCALE_ENUM}`;

export const DEFAULT_LOCALE: SUPPORTED_LOCALE_ENUM = SUPPORTED_LOCALE_ENUM.EN_US;

/**
 * Strip POSIX locale decorations and coerce it toward a BCP-47 tag, handling the
 * legacy shapes seen in `LANG`/`LC_*` environment variables:
 *   - codeset suffixes (`en_US.UTF-8` -> `en_US`);
 *   - `@modifier` suffixes (`sr_RS@latin` -> `sr_RS`);
 *   - underscore separators (`fr_CA` -> `fr-CA`).
 *
 * The special POSIX locales `C` and `POSIX` carry no language information, so they
 * are collapsed to an empty string and will fall through to the default locale.
 *
 * @param tag - the raw locale string, possibly in POSIX form
 */
function stripPosixDecorations(tag: string): string {
  const trimmed = tag.trim();
  if (trimmed === 'C' || trimmed === 'POSIX') {
    return '';
  }

  return trimmed.split('@')[0].split('.')[0].replace(/_/g, '-');
}

/**
 * Normalize a BCP-47 tag (or POSIX legacy locale) and match it to a supported
 * locale, returning:
 *   1. the normalized tag when it is itself supported (`fr-ca`/`fr_CA` -> `fr-CA`);
 *   2. otherwise the first supported locale sharing its base language
 *      (`fr-FR` -> `fr-CA`, `en` -> `en-US`);
 *   3. otherwise `undefined` when nothing supported matches.
 *
 * @param tag - the raw BCP-47 or POSIX locale to match
 */
export function normalizeTag(tag: string): SUPPORTED_LOCALE_ENUM | undefined {
  const [lang, region] = stripPosixDecorations(tag).split('-');
  if (!lang) {
    return undefined;
  }

  const normalized = region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase();

  const exactMatch = SUPPORTED_LOCALES.find((locale) => locale === normalized);
  if (exactMatch) {
    return exactMatch;
  }

  const baseLanguage = normalized.split('-')[0];
  return SUPPORTED_LOCALES.find((locale) => locale.split('-')[0] === baseLanguage);
}

/**
 * Resolve a requested tag (e.g. from `Accept-Language`) to a supported locale,
 * delegating to {@link normalizeTag} and falling back to `DEFAULT_LOCALE` when
 * no tag is provided or nothing supported matches.
 *
 * @param candidate - the requested BCP-47 tag (e.g. from `Accept-Language`)
 */
export function resolveSupportedLocale(candidate?: string | null): SupportedLocale {
  return (candidate ? normalizeTag(candidate) : undefined) ?? DEFAULT_LOCALE;
}
